import React, { useMemo } from "react";
import { type NextPage } from "next";
import Image from "next/image";
import { Box, SimpleGrid, Flex } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import { StackedLogos } from "components/logos";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { CenteredQuote } from "components/quotes";
import { VideoFull } from "components/video-full";
import { getLandingPageProps, getPageMetaImage, getSafeCustomer, type LandingPageProps } from "utils";
import { AuraPlate } from "ui/plates";
import { Button } from "ui/button";
import { Card } from "ui/cards";
import { TextColumn, TextTitle } from "ui/text";
import Balancer from "react-wrap-balancer";
import { MessageIcon, PersonIcon, TrendIcon } from "ui/icons";
import { Diptych } from "ui/diptych";
import { LegacyMediaHero } from "components/hero/legacy-media-hero";
import { integrationsCount } from "../../constants/integrations";
import heroImage from "../../../public/assets/solutions/fivetran/hero.svg";
import introImage from "../../../public/assets/solutions/fivetran/intro.svg";
import firstPairImage from "../../../public/assets/solutions/fivetran/pair-1.svg";
import secondPairImage from "../../../public/assets/solutions/fivetran/pair-2.svg";
import thirdPairImage from "../../../public/assets/solutions/fivetran/pair-3.svg";
import fourthPairImage from "../../../public/assets/solutions/fivetran/pair-4.svg";

const featuredCaseStudies = [
  {
    customerSlug: "weight-watchers",
    title:
      "WeightWatchers partners with Hightouch and Snowflake to boost new member growth by 52% and reduce data onboarding by 2 weeks",
  },
  {
    customerSlug: "blend",
    title: "Blend leveraged unified data to reduce reporting time for Finance and Operations teams by 50%",
  },
  {
    customerSlug: "nandos",
    title:
      "Nandos reduced data integration time by 75% by automating ELT and data activation workflows with Fivetran and Hightouch",
  },
];

const Fivetran: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, customers }) => {
  const blend = getSafeCustomer(customers, "blend");
  const caseStudyCards = useMemo(() => {
    return featuredCaseStudies.map((caseStudy) => {
      const customer = getSafeCustomer(customers, caseStudy.customerSlug);

      return (
        <Card
          key={customer.slug}
          variant="shadow"
          href={`/customers/${customer.slug}`}
          title={caseStudy.title}
          actions={
            <Button hasRightChevron as="p">
              Read customer story
            </Button>
          }
          mediaRatio={2 / 1}
          media={
            <Flex bg={customer.slug} pos="relative" justify="center" align="center" width="100%">
              <Box pos="absolute" filter="brightness(0) invert(1)" zIndex={1} width="50%">
                <Image
                  src={`${customer.logoMonoUrl ?? customer.logoUrl}?w=400`}
                  alt={customer.name + "."}
                  width={200}
                  height={40}
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "auto",
                  }}
                />
              </Box>
            </Flex>
          }
          mb="bigger"
        />
      );
    });
  }, [customers]);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.violet.150"
      footerOptions={{ variant: "violet" }}
    >
      <LegacyMediaHero
        variant="violet"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        imageSrc={heroImage}
        imageAlt="Hightouch and Fivetran logos."
      />

      <StackedLogos customers={defaultLogos} my="0" pt="small" />

      <Section>
        <Box maxW={{ base: "100%", sm: "450px", md: "700px" }} mx="auto">
          <Image
            src={introImage}
            alt="Graph of data moving through Hightouch."
            sizes="100%"
            style={{ objectFit: "contain" }}
          />
        </Box>

        <TextTitle
          size="lg"
          align="center"
          title={<Balancer>Close the loop on your data pipelines with Fivetran & Hightouch</Balancer>}
          actions={
            <Button hasRightChevron href="/playbooks">
              See all Playbooks
            </Button>
          }
          mt={{ base: "huge", md: "huger" }}
        />

        <SimpleGrid columns={{ base: 1, md: 3 }} gap="bigger" mt="biggest" mx={{ base: "0", md: "auto" }}>
          <TextColumn
            iconChip={{ variant: "yellow", icon: PersonIcon }}
            title="Sales & Marketing teams"
            description="Sync customer data to Salesforce, HubSpot, and more."
          />

          <TextColumn
            iconChip={{ variant: "violet", icon: MessageIcon }}
            title="OPS & GTM teams"
            description="Get real-time notifications in tools like Slack and Microsoft Teams."
          />

          <TextColumn
            iconChip={{ variant: "blue", icon: TrendIcon }}
            title="Advertising teams"
            description="Optimize ad campaign performance on platforms such as Facebook."
          />
        </SimpleGrid>
      </Section>

      <Section>
        <Flex flexDir="column" gap={sectionSpacingY}>
          <Diptych
            direction="right"
            mediaRatio={290 / 171}
            media={
              <Box p="small">
                <Image src={firstPairImage} alt="" />
              </Box>
            }
            titleAs="h2"
            title="Build your integrations off the warehouse"
            description={`The cloud data warehouse is quickly becoming the organizational source of truth. Ensure your data store
              doesn’t become a data silo: it’s not just about getting the data in, but also delivering it to the teams
              that will make decisions on it. As the last mile of the modern data stack, Reverse ETL activates that
              otherwise stagnant data by automatically syncing it into ${integrationsCount}+ business apps.`}
            actions={
              <Button hasRightChevron href="/resources/data-integration-whitepaper">
                Read our joint whitepaper with Fivetran
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={593 / 322}
            media={
              <Box p="small">
                <Image src={secondPairImage} alt="" />
              </Box>
            }
            titleAs="h2"
            title="Trigger syncs as soon as your Fivetran jobs complete"
            description="Keep the freshest data possible flowing into your operational tools. With our native Fivetran integration, you can trigger Hightouch syncs to run as soon as upstream Fivetran connectors or Fivetran dbt transformations complete."
            actions={
              <Button hasRightChevron href="/blog/fivetran-extension">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="right"
            mediaRatio={453 / 309}
            media={
              <Box p="small">
                <Image src={thirdPairImage} alt="" />
              </Box>
            }
            titleAs="h2"
            title="Version control with Git"
            description="Use GitHub or Bitbucket to bi-directionally sync all configuration changes from Hightouch or your CLI. You can see all edits and rollback unintended changes immediately and require PRs to ensure all changes are approved."
            actions={
              <Button hasRightChevron href="/blog/announcing-git-sync-back-your-syncs-with-version-control">
                Learn more
              </Button>
            }
          />

          <Diptych
            direction="left"
            mediaRatio={34 / 33}
            media={
              <Box p="small">
                <Image src={fourthPairImage} alt="" />
              </Box>
            }
            titleAs="h2"
            title="We are a Fivetran Partner of the Year"
            description="Over 150 data-driven organizations trust Fivetran and Hightouch to turn their data warehouse into an activation engine. Join the movement and power incredible customer experiences by super-charging your ELT investments with Reverse ETL."
            actions={
              <Button hasRightChevron href="/blog/fivetran-ecosystem-partner-of-the-year">
                Learn more
              </Button>
            }
          />
        </Flex>
      </Section>

      <AuraPlate condensed variant="violet" py={0}>
        <CenteredQuote
          author={{
            name: "William Tsu",
            job: "Customer Success Operations",
          }}
          company={{
            name: "Blend",
            logo: blend.logoUrl,
          }}
          quoteText="The value of Fivetran isn’t any one individual connector — it’s being able to pull in Salesforce, Marketo, Asana, and NetSuite and blend the data from historically separate departments together for analysis. With Hightouch, we can then push it out to make sure that everyone’s looking at the same metrics."
          cta={{ href: "/customers/blend", text: "Read customer story" }}
        />
      </AuraPlate>

      <VideoFull
        sectionTitle="See how you can synchronize your Fivetran and Hightouch pipelines"
        source="https://cdn.sanity.io/files/pwmfmi47/production/05f48da576ab4ec1cd867525c68931a70932fa79.mp4"
        poster="/assets/solutions/fivetran/video-poster.webp"
        title="Synchronize your Fivetran and Hightouch jobs"
        description="Watch this video and learn how to synchronize Fivetran and Hightouch jobs in 2 minutes."
        duration="2 mins"
        onPlay={() => {
          trackEvent("Fivetran Video Played");
        }}
        onEnded={() => {
          trackEvent("Fivetran Video Ended");
        }}
      />

      <Section my={0} pb={sectionSpacingY}>
        <TextTitle
          size="lg"
          align="center"
          title={
            <Balancer>Learn how modern teams are automating their data pipelines with Fivetran and Hightouch</Balancer>
          }
          maxW="5xl"
          mb={{ base: "bigger", md: "biggest" }}
        />

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} columnGap="small" justifyItems="center">
          {caseStudyCards}
        </SimpleGrid>
      </Section>
    </Page>
  );
};

export default Fivetran;

export const getStaticProps = getLandingPageProps("/solutions/fivetran", {
  loadDefaultLogos: true,
  loadCustomers: featuredCaseStudies.map((caseStudy) => caseStudy.customerSlug),
});
